import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

import useNavigateWithSearch from '../../../../../../hooks/useNavigateWithSearch';
import { useFirebaseAuth } from '../../../../../global/FirebaseProvider/FirebaseProvider';
import useTbbPaymentConfig from '../../../../../../hooks/useTbbPaymentConfig';
import { Button, BUTTON_SIZE, BUTTON_TYPE } from '../../../../global/Button';
import LoadingBar from '../../../../global/LoadingBar';
import RewardsV2Card from '../../../../global/RewardsV2Card';
import { REWARD_STATUSES } from '../../../../config/rewards';
import {
  ROUTE_DEALS,
  ROUTE_REWARDS_V2_STORE_REWARD_ID,
  STORE_ID_ROUTE_PARAM,
  REWARD_ID_ROUTE_PARAM,
} from '../../../../config/routes';
import { TBB_PAYMENT_CONFIG_ITEMS, filterOneRewardPerStore, insertTCIcon } from '../../../../utils/tbbRegistration';
import { getRewardsOffer } from '../../../../utils/service';
import RegistrationSuccess from '../../Components/Animations/RegistrationSuccess';
import ExploreMoreRewards from './ExploreMoreRewards';
import ReadyToShip from './ReadyToShip';
import RewardsV2Shelf from '../../../../global/RewardsV2Shelf';
import GiftCardsPreviewList from '../../../GiftCards/GiftCardsPreviewList';
import UnlimitedShortcutBannerCard from '../../../TakeBackBags/UnlimitedShortcutBannerCard';
import './PostRegistration.scss';

export default function PostRegistration({
  storeConfig,
  onClick,
  shippingMethod,
  address,
  stepTrackerProps,
  redemptionCode,
  showSuccessAnimation,
  isTechRecycling,
  isPayOnShip = false,
  isLoadingPayOnShipConfig = false,
  payOnShipConfig = {},
}) {
  const navigateWithSearch = useNavigateWithSearch();
  const { userData } = useFirebaseAuth();
  const { isLoading: isLoadingTbbPaymentConfig, tbbPaymentConfig } = useTbbPaymentConfig();

  const [showRegistrationSuccess, setShowRegistrationSuccess] = useState(showSuccessAnimation);
  const [payOnShipDeals, setPayOnShipDeals] = useState([]);
  const [isLoadingDeals, setIsLoadingDeals] = useState(false);

  useEffect(() => {
    if (!isPayOnShip || isLoadingPayOnShipConfig) return undefined;

    let cancelled = false;

    const fetchDeals = async () => {
      setIsLoadingDeals(true);
      const result = await getRewardsOffer({
        page: 1,
        pageSize: 50,
        sort: 'sortRank',
        status: REWARD_STATUSES.ACTIVE,
        exchangeValueMax: 0,
      });

      if (cancelled) return;

      if (!result.error && result.data?.rewards) {
        const uniqueDeals = filterOneRewardPerStore(result.data.rewards);
        const filtered = uniqueDeals.filter((reward) => {
          const storeTitle = (reward.store?.title || '').toLowerCase();
          const { PAY_ON_SHIP_DEAL_BRANDS: brands = [] } = payOnShipConfig;
          return brands.some((brand) => storeTitle.includes(brand));
        });
        setPayOnShipDeals(filtered);
      }
      setIsLoadingDeals(false);
    };

    fetchDeals();

    return () => { cancelled = true; };
  }, [isPayOnShip, isLoadingPayOnShipConfig, payOnShipConfig]);

  const handleDealClick = useCallback((rewardItem) => {
    const path = ROUTE_REWARDS_V2_STORE_REWARD_ID
      .replace(STORE_ID_ROUTE_PARAM, rewardItem.store?.id)
      .replace(REWARD_ID_ROUTE_PARAM, rewardItem.uid);
    navigateWithSearch(path);
  }, [navigateWithSearch]);

  const items = useMemo(
    () => tbbPaymentConfig.items
      ?.map((item) => {
        switch (item.id) {
          case TBB_PAYMENT_CONFIG_ITEMS.deals_shelves:
            return {
              key: item.id,
              title: item.titlePostRegistration,
              content: (
                <div className="PostRegistration__dealsShelves">
                  {
                    item?.children?.map((child) => (
                      <RewardsV2Shelf
                        title={insertTCIcon(child.title, 24)}
                        type={child?.type}
                        defaultSlidesPerPage={2}
                        itemCardBackgroundColor="var(--trashie-ui-crisp-white)"
                        advancedMode
                        fullWidth
                        mobileGap="0.5rem"
                      />
                    ))
                  }
                </div>
              ),
            };
          case TBB_PAYMENT_CONFIG_ITEMS.unlock_rewards:
            return {
              key: item.id,
              title: item.titlePostRegistration,
              content: (
                <ExploreMoreRewards
                  title={item.titlePostRegistration}
                  storeConfig={storeConfig}
                  className="PostRegistration__explore-rewards"
                  buttonText="Discover now"
                  buttonType={BUTTON_TYPE.QUATERNARY}
                  isRewards={false}
                />
              ),
            };

          case TBB_PAYMENT_CONFIG_ITEMS.gift_cards:
            return {
              key: item.id,
              content: (
                <div className="PostRegistration__gift-cards">
                  <GiftCardsPreviewList
                    title={insertTCIcon(item?.titlePostRegistration, 24)}
                  />
                </div>
              ),
            };

          default:
            return null;
        }
      })
      .filter(Boolean),
    [tbbPaymentConfig, storeConfig, redemptionCode],
  );

  if (showRegistrationSuccess && storeConfig.isTrashie) {
    return (
      <RegistrationSuccess
        isTechRecycling={isTechRecycling}
        onShowingAnimation={setShowRegistrationSuccess}
      />
    );
  }

  return (
    <div className="PostRegistration">
      <ReadyToShip
        onClick={onClick}
        shippingMethod={shippingMethod}
        address={address}
        stepTrackerProps={stepTrackerProps}
        storeConfig={storeConfig}
      />
      {isPayOnShip && !userData?.activeSubscription && (
        <div className="PostRegistration__section">
          <h3 className="PostRegistration__section-title">Unlimited Membership</h3>
          <UnlimitedShortcutBannerCard className="PostRegistration__unlimitedBanner" />
        </div>
      )}
      {isPayOnShip && (
        <div className="PostRegistration__topDeals">
          <div className="PostRegistration__topDeals-header">
            <h2 className="PostRegistration__topDeals-title">
              <span>Shop Deals</span>
              <span>Earn Points</span>
            </h2>
            <p className="PostRegistration__topDeals-description">
              <span>When you donate with Trashie you</span>
              <span>unlock a world of deals (and points)</span>
            </p>
          </div>
          <p className="PostRegistration__topDeals-subtitle">Top Deals</p>
          {isLoadingDeals ? (
            <LoadingBar />
          ) : (
            <div className="PostRegistration__topDeals-grid">
              {payOnShipDeals.map((deal, index) => (
                <RewardsV2Card
                  key={deal.uid}
                  index={index}
                  rewardItem={deal}
                  onClick={handleDealClick}
                  backgroundColor="var(--trashie-ui-crisp-white)"
                />
              ))}
            </div>
          )}
          <Button
            type={BUTTON_TYPE.TERTIARY}
            size={BUTTON_SIZE.LARGE}
            onClick={() => navigateWithSearch(ROUTE_DEALS)}
          >
            Explore Deals
          </Button>
        </div>
      )}
      {!isPayOnShip && (
        isLoadingTbbPaymentConfig ? (
          <LoadingBar />
        ) : (
          <>
            {items.map((item) => <Fragment key={item.key}>{item.content}</Fragment>)}
          </>
        )
      )}
    </div>
  );
}
